import {lazy, Suspense, useCallback, useEffect, useMemo, useState} from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import {
  Archive,
  Download,
  Eye,
  FileText,
  Film,
  Image as ImageIcon,
  Music,
  RefreshCw,
  Sheet,
  Trash2,
  X,
} from 'lucide-react'
import {
  attachmentKindLabels,
  formatAttachmentSize,
  getAttachmentPreviewKind,
  prepareAttachmentPreview,
  type AttachmentMetadata,
  type AttachmentPreviewKind,
} from '../data/attachmentPreview'
import {
  downloadAttachmentBlob,
  downloadTopicAttachment,
  loadTopicAttachment,
  type TopicAttachment,
} from '../services/topicsApi'

const TopicPdfPreview = lazy(() => import('./TopicPdfPreview'))
const TopicSpreadsheetPreview = lazy(() => import('./TopicSpreadsheetPreview'))
const TopicTextPreview = lazy(() => import('./TopicTextPreview'))

type Props = {
  files?: File[]
  attachments?: TopicAttachment[]
  topicId?: string
  requestId?: string
  onRemove?: (file: File) => void
  removeDisabled?: boolean
}

function AttachmentIcon({
  kind,
  name,
  size = 34,
}: {
  kind: AttachmentPreviewKind
  name: string
  size?: number
}) {
  const Icon =
    kind === 'image'
      ? ImageIcon
      : kind === 'video'
      ? Film
      : kind === 'audio'
      ? Music
      : kind === 'spreadsheet'
      ? Sheet
      : /\.(zip|rar|7z)$/i.test(name)
      ? Archive
      : FileText
  return <Icon size={size} aria-hidden='true' />
}

function AttachmentCard({
  file,
  attachment,
  topicId,
  requestId,
  onRemove,
  removeDisabled,
}: {
  file?: File
  attachment?: TopicAttachment
  topicId?: string
  requestId?: string
  onRemove?: (file: File) => void
  removeDisabled?: boolean
}) {
  const name = file?.name || attachment?.name || 'Arquivo'
  const size = file?.size ?? attachment!.size
  const mimeType = file?.type || attachment?.mimeType || ''
  const metadata = useMemo<AttachmentMetadata>(
    () => ({name, size, mimeType}),
    [name, size, mimeType]
  )
  const kind = getAttachmentPreviewKind(metadata)
  const canPreview = kind !== 'unsupported'
  const [open, setOpen] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [thumbnailError, setThumbnailError] = useState(false)
  const [previewError, setPreviewError] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState('')
  const [resource, setResource] = useState<{
    loading: boolean
    url?: string
    blob?: Blob
    error?: string
  }>({loading: false})
  const enabled = canPreview && (open || kind === 'image' || kind === 'pdf')
  const attachmentId = attachment?.id
  const savedAttachment = useMemo(
    () => (attachmentId ? {id: attachmentId, ...metadata} : undefined),
    [attachmentId, metadata]
  )

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    let url = ''
    setResource({loading: true})
    setThumbnailError(false)
    const load = file
      ? Promise.resolve(file)
      : loadTopicAttachment(topicId!, requestId!, savedAttachment!, controller.signal)
    load
      .then((blob) => prepareAttachmentPreview(blob, metadata, controller.signal))
      .then((preview) => {
        if (controller.signal.aborted) return
        url = URL.createObjectURL(preview.blob)
        setResource({loading: false, blob: preview.blob, url})
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setResource({
            loading: false,
            error: error.message || 'Não foi possível carregar a prévia.',
          })
      })
    return () => {
      controller.abort()
      if (url) URL.revokeObjectURL(url)
    }
  }, [file, topicId, requestId, savedAttachment, metadata, enabled, attempt])

  const failPreview = useCallback(() => setPreviewError(true), [])
  const failThumbnail = useCallback(() => setThumbnailError(true), [])
  const openPreview = () => {
    setPreviewError(false)
    setDownloadError('')
    setOpen(true)
  }
  const retry = () => {
    setPreviewError(false)
    setThumbnailError(false)
    setAttempt((value) => value + 1)
  }
  const download = async () => {
    if (downloading) return
    setDownloading(true)
    setDownloadError('')
    try {
      if (file || resource.blob) downloadAttachmentBlob(file || resource.blob!, name)
      else await downloadTopicAttachment(topicId!, requestId!, attachment!)
    } catch (error) {
      setDownloadError(
        (error as Error).message || 'Não foi possível baixar o anexo. Tente novamente.'
      )
    } finally {
      setDownloading(false)
    }
  }
  const waiting = (
    <Box
      role='status'
      aria-label={'Carregando prévia de ' + name}
      sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, p: 3}}
    >
      <CircularProgress size={24} />
      <Typography variant='body2' color='text.secondary'>
        Carregando prévia...
      </Typography>
    </Box>
  )
  const hasThumbnail =
    !resource.error && !thumbnailError && !!resource.url && (kind === 'image' || kind === 'pdf')
  const label = attachmentKindLabels[kind]
  const errorMessage =
    resource.error ||
    (previewError
      ? 'Não foi possível exibir este arquivo. O formato pode não ser compatível ou o arquivo pode estar protegido.'
      : '')
  const downloadButton = (
    <Button
      size='small'
      onClick={download}
      disabled={downloading}
      startIcon={downloading ? <CircularProgress size={15} /> : <Download size={15} />}
      aria-label={'Baixar anexo ' + name}
    >
      {downloading ? 'Baixando...' : 'Baixar'}
    </Button>
  )

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          bgcolor: 'white',
          overflow: 'hidden',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ButtonBase
          disabled={!canPreview}
          onClick={openPreview}
          aria-label={'Abrir prévia de ' + name}
          sx={{
            display: 'flex',
            width: '100%',
            height: 128,
            bgcolor: '#f1f5f9',
            color: 'text.secondary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            '&:hover': {bgcolor: '#eaf2ff'},
            '&.Mui-focusVisible': {outline: '2px solid #2563eb', outlineOffset: -2},
          }}
        >
          {resource.loading ? (
            <CircularProgress size={22} aria-label='Carregando miniatura' />
          ) : hasThumbnail && kind === 'image' ? (
            <Box
              component='img'
              src={resource.url}
              alt={'Miniatura de ' + name}
              onError={failThumbnail}
              sx={{width: '100%', height: '100%', objectFit: 'contain', p: 1}}
            />
          ) : hasThumbnail && kind === 'pdf' ? (
            <Suspense fallback={<CircularProgress size={22} />}>
              <TopicPdfPreview url={resource.url!} thumbnail onError={failThumbnail} />
            </Suspense>
          ) : (
            <Box sx={{display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 1}}>
              <AttachmentIcon kind={kind} name={name} />
              <Typography variant='caption'>
                {resource.error || thumbnailError ? 'Miniatura indisponível' : label}
              </Typography>
            </Box>
          )}
        </ButtonBase>
        <Box sx={{p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75}}>
          <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 0.5}}>
            <Typography
              variant='body2'
              fontWeight={600}
              sx={{overflowWrap: 'anywhere', flex: 1, minWidth: 0}}
            >
              {name}
            </Typography>
            {file && onRemove && (
              <IconButton
                size='small'
                aria-label={'Remover anexo ' + name}
                disabled={removeDisabled}
                onClick={() => onRemove(file)}
                sx={{mt: -0.5, mr: -0.5, color: 'text.secondary'}}
              >
                <Trash2 size={16} />
              </IconButton>
            )}
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {label} · {formatAttachmentSize(size)}
          </Typography>
          {!canPreview && (
            <Typography variant='caption' color='text.secondary'>
              Prévia não disponível para este formato.
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.75,
              mt: 'auto',
              pt: 1,
            }}
          >
            <Button
              size='small'
              variant='outlined'
              startIcon={<Eye size={15} />}
              disabled={!canPreview}
              onClick={openPreview}
              aria-label={'Visualizar anexo ' + name}
              sx={{minHeight: 34}}
            >
              Visualizar
            </Button>
            {downloadButton}
          </Box>
          {downloadError && (
            <Alert severity='error' sx={{mt: 1, '& .MuiAlert-message': {overflowWrap: 'anywhere'}}}>
              {downloadError}
            </Alert>
          )}
        </Box>
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='lg'
        fullWidth
        aria-label={'Visualizar anexo ' + name}
        PaperProps={{
          'aria-label': 'Visualizar anexo ' + name,
          'aria-labelledby': undefined,
          sx: {
            m: {xs: 1.5, sm: 3},
            width: {xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)'},
            maxHeight: 'calc(100dvh - 24px)',
            borderRadius: 2.5,
          },
        }}
      >
        <DialogTitle
          component='div'
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: {xs: 2, sm: 2.5},
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{color: 'primary.main', mt: 0.5, display: {xs: 'none', sm: 'flex'}}}>
            <AttachmentIcon kind={kind} name={name} size={23} />
          </Box>
          <Box sx={{minWidth: 0, flex: 1}}>
            <Typography
              component='h2'
              variant='subtitle1'
              fontWeight={600}
              sx={{overflowWrap: 'anywhere'}}
            >
              {name}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {label} · {formatAttachmentSize(size)}
            </Typography>
          </Box>
          <IconButton aria-label='Fechar visualização' onClick={() => setOpen(false)} size='small'>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: {xs: 2, sm: 2.5},
            bgcolor: '#f8fafc',
            minHeight: 180,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{width: '100%', minWidth: 0}}>
            {downloadError && (
              <Alert severity='error' sx={{mb: 2}}>
                {downloadError}
              </Alert>
            )}
            {errorMessage ? (
              <Box sx={{py: 3}}>
                <Alert severity='warning' sx={{mb: 2}}>
                  {errorMessage}
                </Alert>
                <Button variant='outlined' startIcon={<RefreshCw size={16} />} onClick={retry}>
                  Tentar novamente
                </Button>
              </Box>
            ) : resource.loading || !resource.url ? (
              waiting
            ) : (
              <Suspense fallback={waiting}>
                {kind === 'image' && (
                  <Box sx={{display: 'flex', justifyContent: 'center'}}>
                    <Box
                      component='img'
                      src={resource.url}
                      alt={'Prévia de ' + name}
                      onError={failPreview}
                      sx={{
                        display: 'block',
                        maxWidth: '100%',
                        maxHeight: '65dvh',
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                )}
                {kind === 'pdf' && (
                  <TopicPdfPreview key={resource.url} url={resource.url} onError={failPreview} />
                )}
                {kind === 'text' && (
                  <TopicTextPreview
                    key={resource.url}
                    blob={resource.blob!}
                    onError={failPreview}
                  />
                )}
                {kind === 'spreadsheet' && (
                  <TopicSpreadsheetPreview
                    key={resource.url}
                    blob={resource.blob!}
                    name={name}
                    onError={failPreview}
                  />
                )}
                {kind === 'video' && (
                  <Box
                    component='video'
                    src={resource.url}
                    controls
                    preload='metadata'
                    onError={failPreview}
                    aria-label={'Vídeo ' + name}
                    sx={{display: 'block', width: '100%', maxHeight: '65dvh'}}
                  />
                )}
                {kind === 'audio' && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'column',
                      gap: 3,
                      py: 4,
                    }}
                  >
                    <Music size={48} color='#64748b' aria-hidden='true' />
                    <Box
                      component='audio'
                      src={resource.url}
                      controls
                      preload='metadata'
                      onError={failPreview}
                      aria-label={'Áudio ' + name}
                      sx={{width: '100%', maxWidth: 560}}
                    />
                  </Box>
                )}
              </Suspense>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{borderTop: '1px solid', borderColor: 'divider', p: {xs: 1.5, sm: 2}, gap: 1}}
        >
          <Button onClick={() => setOpen(false)}>Fechar</Button>
          {downloadButton}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default function TopicAttachmentList({
  files,
  attachments,
  topicId,
  requestId,
  onRemove,
  removeDisabled,
}: Props) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        },
        gap: 1.5,
      }}
    >
      {files?.map((file) => (
        <AttachmentCard
          key={file.name + ':' + file.size + ':' + file.lastModified}
          file={file}
          onRemove={onRemove}
          removeDisabled={removeDisabled}
        />
      ))}
      {attachments?.map((attachment) => (
        <AttachmentCard
          key={requestId + ':' + attachment.id}
          attachment={attachment}
          topicId={topicId}
          requestId={requestId}
        />
      ))}
    </Box>
  )
}
